// Inventering → Inventeringsperiod (owner 2026-11-04/05, mockup admin-v1/count-period): the year's count period, set
// by hand (about four months), and whether every batch in stock is done for it — counted within the period (every
// carton by Trail, or the ERP's own date: Datum 1 at Kalundgren) or received after it started. Articles without batch
// handling are counted as a quantity per article and stand as one line. What is left can become a count round of the
// places where it stands (the whole place is always counted).

import { useEffect, useMemo, useState } from "react";
import { Admin, Api, type CountPeriodItem, type CountPeriodStatus, type ReferenceBundle } from "../api/client.js";
import { fmtQty, t } from "../i18n.js";

type Filter = "left" | "partial" | "done" | "unplaced";
const SHOWN = 300;
const day = (iso: string | null) => (iso ? iso.slice(0, 10) : null);
const daysBetween = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000);

export function CountPeriod({ bundle, onPlan }: { bundle: ReferenceBundle; onPlan: (locationIds: string[], name: string) => void }) {
  const [data, setData] = useState<CountPeriodStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [viewing, setViewing] = useState<{ from: string; to: string } | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [filter, setFilter] = useState<Filter>("left");
  const [zone, setZone] = useState("");
  const [q, setQ] = useState("");
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setErr(null);
    Api.countPeriod(viewing).then((d) => {
      setData(d);
      if (!viewing) { setFrom(d.period?.from ?? ""); setTo(d.period?.to ?? ""); }
    }).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [viewing, tick]);

  const siteId = bundle.siteId ?? bundle.sites[0]?.id ?? null;
  const savePeriods = async (periods: { from: string; to: string }[]) => {
    if (!siteId) return;
    setBusy(true);
    try {
      const cur = await Admin.siteConfig(siteId);
      await Admin.putSiteConfig(siteId, { ...cur.config, counting: { ...cur.config.counting, periods } });
      setViewing(null); setTick((n) => n + 1);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to;
  const periods = data?.periods ?? [];
  const current = periods[periods.length - 1] ?? null;
  const changed = !!current && (current.from !== from || current.to !== to);
  const saveCurrent = () => void savePeriods(current ? [...periods.slice(0, -1), { from, to }] : [{ from, to }]);
  const addNew = () => void savePeriods([...periods, { from, to }].sort((a, b) => a.from.localeCompare(b.from)));

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.items ?? []).filter((x) => x.status === filter && (!zone || x.zone === zone) && (!needle || `${x.sku ?? ""} ${x.name ?? ""} ${x.lot ?? ""} ${x.locations.map((l) => l.code).join(" ")}`.toLowerCase().includes(needle)));
  }, [data, filter, zone, q]);
  const placesOf = (keys: Set<string>) => [...new Set((data?.items ?? []).filter((x) => keys.has(x.key)).flatMap((x) => x.locations.map((l) => l.id)))];
  const chosenPlaces = placesOf(chosen);
  const toggle = (k: string) => setChosen((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const allShown = items.slice(0, SHOWN).filter((x) => x.locations.length > 0);
  const allChosen = allShown.length > 0 && allShown.every((x) => chosen.has(x.key));

  if (err && !data) return <div className="card"><div className="bd" style={{ color: "var(--bad)" }}>{err}</div></div>;
  if (!data) return <div className="card"><div className="bd"><p className="ahint">{t("common.loading")}</p></div></div>;

  const p = data.period;
  const tot = data.totals;
  const placed = tot.all - tot.unplaced;
  const pct = (n: number, of: number) => (of ? Math.round((n / of) * 100) : 0);
  const today = new Date().toISOString().slice(0, 10);
  const left = p ? daysBetween(today, p.to) : null;
  const months = p ? Math.max(1, Math.round(daysBetween(p.from, p.to) / 30.4)) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="card">
        <div className="cp-period">
          <div>
            <div className="cp-k">{viewing ? t("adm.cp.viewing") : t("adm.cp.current")}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input className="ain" type="date" value={from} onChange={(e) => setFrom(e.target.value)} disabled={!!viewing} style={{ width: 150 }} />
              <span>–</span>
              <input className="ain" type="date" value={to} onChange={(e) => setTo(e.target.value)} disabled={!!viewing} style={{ width: 150 }} />
            </div>
            {!viewing && (
              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                {(!current || changed) && <button type="button" className="abtn sm primary" disabled={!valid || busy} onClick={saveCurrent}>{current ? t("adm.cp.save") : t("adm.cp.set")}</button>}
                {current && changed && <button type="button" className="abtn sm" disabled={!valid || busy || from <= current.to} onClick={addNew} title={t("adm.cp.addNew.hint")}>{t("adm.cp.addNew")}</button>}
              </div>
            )}
            <div className="mut" style={{ fontSize: 13, marginTop: 8 }}>
              {p ? t("adm.cp.span", { months: months ?? 0 }) : t("adm.cp.none")}
              {p && left !== null ? ` · ${left >= 0 ? t("adm.cp.daysLeft", { n: left }) : t("adm.cp.ended")}` : ""}
            </div>
            {periods.length > 1 && (
              <label className="mut" style={{ fontSize: 13, display: "flex", gap: 6, alignItems: "center", marginTop: 8 }}>{t("adm.cp.show")}
                <select value={viewing ? `${viewing.from}|${viewing.to}` : ""} onChange={(e) => { const [f, tt] = e.target.value.split("|"); setViewing(f && tt ? { from: f, to: tt } : null); setChosen(new Set()); }}>
                  <option value="">{t("adm.cp.currentOpt")}</option>
                  {periods.slice(0, -1).reverse().map((x) => <option key={x.from} value={`${x.from}|${x.to}`}>{x.from} – {x.to}</option>)}
                </select>
              </label>
            )}
          </div>
          <div>
            <div className="cp-k">{t("adm.cp.doneTitle")}</div>
            {p ? (
              <>
                <div className="cp-big">{tot.done} <small>{t("adm.cp.of", { n: placed + tot.unplaced, pct: pct(tot.done, tot.all) })}{tot.receivedAfter ? ` · ${t("adm.cp.receivedAfter", { n: tot.receivedAfter, date: p.from })}` : ""}</small></div>
                <div className="cp-bar" style={{ marginTop: 8 }}><i style={{ width: `${pct(tot.done, tot.all)}%` }} /><i className="part" style={{ width: `${pct(tot.partial, tot.all)}%` }} /></div>
                <div className="cp-zones">
                  {data.zones.map((z) => (
                    <div key={z.code}><b className="id">{z.code}</b><div className="cp-bar" style={{ margin: "4px 0" }}><i style={{ width: `${pct(z.done, z.all)}%` }} /><i className="part" style={{ width: `${pct(z.partial, z.all)}%` }} /></div><div className="mut" style={{ fontSize: 12.5 }}>{t("adm.cp.zoneOf", { done: z.done, all: z.all })}{z.partial ? ` · ${t("adm.cp.partialN", { n: z.partial })}` : ""}</div></div>
                  ))}
                  <div><b>{t("adm.cp.unplaced")}</b><div className="cp-bar" style={{ margin: "4px 0" }} /><div className="mut" style={{ fontSize: 12.5 }}>{tot.unplaced}</div></div>
                </div>
              </>
            ) : <p className="ahint" style={{ margin: 0 }}>{t("adm.cp.setFirst")}</p>}
          </div>
        </div>
      </div>

      {p && (
        <>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <span className="seg">
              {(["left", "partial", "done", "unplaced"] as Filter[]).map((f) => (
                <button key={f} type="button" className={filter === f ? "on" : ""} onClick={() => { setFilter(f); setChosen(new Set()); }}>{t(`adm.cp.f.${f}` as "adm.cp.f.left")} <span className="mut">{f === "left" ? tot.left : f === "partial" ? tot.partial : f === "done" ? tot.done : tot.unplaced}</span></button>
              ))}
            </span>
            <select value={zone} onChange={(e) => setZone(e.target.value)} style={{ width: "auto" }}>
              <option value="">{t("adm.cp.allZones")}</option>
              {data.zones.map((z) => <option key={z.code} value={z.code}>{z.code}</option>)}
            </select>
            <input className="ain" style={{ width: 220 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("adm.cp.search")} />
            <span style={{ flex: 1 }} />
            {!viewing && filter !== "done" && filter !== "unplaced" && (
              <button type="button" className="abtn primary" disabled={chosenPlaces.length === 0} onClick={() => onPlan(chosenPlaces, t("adm.cp.roundName", { date: today }))}>{t("adm.cp.plan", { n: chosenPlaces.length })}</button>
            )}
          </div>
          <div className="card">
            <div className="bd flush">
              <table className="at">
                <thead><tr>
                  <th style={{ width: 32 }}>{filter !== "done" && filter !== "unplaced" && <input type="checkbox" checked={allChosen} onChange={() => setChosen(allChosen ? new Set() : new Set(allShown.map((x) => x.key)))} />}</th>
                  <th>{t("adm.cp.col.item")}</th><th>{t("adm.cp.col.place")}</th><th className="num">{t("adm.cp.col.qty")}</th><th>{t("adm.cp.col.last")}</th><th>{t("adm.cp.col.state")}</th>
                </tr></thead>
                <tbody>
                  {items.length === 0 && <tr><td colSpan={6} className="mut">{t("adm.cp.empty")}</td></tr>}
                  {items.slice(0, SHOWN).map((x) => <Row key={x.key} x={x} period={p} chosen={chosen.has(x.key)} onToggle={filter === "done" || filter === "unplaced" || x.locations.length === 0 ? null : () => toggle(x.key)} />)}
                </tbody>
              </table>
            </div>
            {items.length > SHOWN && <div className="bd"><p className="ahint" style={{ margin: 0 }}>{t("adm.cp.more", { shown: SHOWN, n: items.length })}</p></div>}
          </div>
          <p className="ahint" style={{ margin: 0 }}>{t("adm.cp.hint")}</p>
        </>
      )}
    </div>
  );
}

function Row({ x, period, chosen, onToggle }: { x: CountPeriodItem; period: { from: string; to: string }; chosen: boolean; onToggle: (() => void) | null }) {
  const trail = day(x.trailCountedAt);
  const last = [trail, x.erpCountedOn].filter((v): v is string => !!v).sort().pop() ?? null;
  const src = last && last === trail ? "trail" : last ? "erp" : null;
  const pill = x.status === "done" ? <span className="pill ok">{x.doneBy === "received" ? t("adm.cp.s.new") : t("adm.cp.s.done")}</span>
    : x.status === "partial" ? <span className="pill warn">{t("adm.cp.s.partial")}</span>
    : x.status === "unplaced" ? <span className="pill mut">{t("adm.cp.s.unplaced")}</span>
    : <span className="pill bad">{t("adm.cp.s.left")}</span>;
  const places = x.locations.slice(0, 2).map((l) => l.code).join(", ");
  return (
    <tr>
      <td>{onToggle && <input type="checkbox" checked={chosen} onChange={onToggle} />}</td>
      <td><b>{x.sku ?? "?"}</b> {x.name ?? ""}<div className="mut" style={{ fontSize: 12.5 }}>{x.lot ? t("adm.cp.batch", { lot: x.lot }) : t("adm.cp.article")}{x.cartons > 1 ? ` · ${t(x.lot ? "adm.cp.cartons" : "adm.cp.rows", { n: x.cartons })}` : ""}</div></td>
      <td className="id">{places || "—"}{x.locations.length > 2 ? <span className="mut"> +{x.locations.length - 2}</span> : null}{x.unplaced > 0 && x.locations.length > 0 ? <div className="mut" style={{ fontSize: 12 }}>{t("adm.cp.plusUnplaced", { n: x.unplaced })}</div> : null}</td>
      <td className="num">{x.quantity.map((q) => fmtQty(q.value, q.uom)).join(" + ")}</td>
      <td>{last ?? <span className="mut">{t("adm.cp.never")}</span>}{src && <span className="cp-src">{src === "trail" ? "Trail" : t("adm.cp.src.erp")}</span>}
        {x.status === "partial" && <div className="mut" style={{ fontSize: 12 }}>{t("adm.cp.partialWhat")}</div>}
        {x.doneBy === "received" && x.receivedOn && <div className="mut" style={{ fontSize: 12 }}>{t("adm.cp.receivedOn", { date: x.receivedOn })}</div>}
        {x.status !== "done" && last && last < period.from && <div className="mut" style={{ fontSize: 12 }}>{t("adm.cp.before")}</div>}
      </td>
      <td>{pill}</td>
    </tr>
  );
}
